import React, {
  useState,
  useEffect,
  FC,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTracking } from '../hooks/useTracking';
import logService, { ActionCategory } from '../services/logging/logService';
import classNames from "classnames";
import moment from "moment";
import BillingHeader from "../components/BillingHeader";
import BillingChart from "../components/BillingChart";
import { downloadCSV } from "./print/billed/BilledCSV";
import {
  BilledCategory,
  BilledCustomer,
  BilledDetailRow,
  Agent,
  BilledResponse,
  SortableHeaderProps,
  SumData,
  ChartData,
} from "../types/billed";
import { SelectOption } from "../types/documents";
import { handleLogout } from "../config/axios";
import decryptData from "../decrypt";
import BeatLoader from "react-spinners/BeatLoader";
import { fetchUserInfo, fetchClientsByAgentCodes, prepareAgentCodes, hasMultipleCodes, splitAgentCodes } from "../services/userService";
import { apiFetch } from '../config/fetch';

// Component for sortable table headers
const SortableHeader: FC<SortableHeaderProps> = ({
  field,
  label,
  onClick,
  sortField,
  sortDirection,
}) => {
  const isSorted = sortField === field;
  return (
    <th
      className="cursor-pointer px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
      onClick={() => onClick(field)}
    >
      {label}
      {isSorted && <span>{sortDirection === "asc" ? " 🔼" : " 🔽"}</span>}
    </th>
  );
};

// Define types for additional data

// Utility to prevent duplicate API calls
const useDebounce = <T extends any[]>(
  fn: (...args: T) => Promise<void>,
  delay: number
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCallingRef = useRef<boolean>(false);

  return useCallback(
    (...args: T) => {
      if (isCallingRef.current) return Promise.resolve();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      return new Promise<void>((resolve) => {
        timeoutRef.current = setTimeout(() => {
          isCallingRef.current = true;
          fn(...args).finally(() => {
            isCallingRef.current = false;
            resolve();
          });
        }, delay);
      });
    },
    [fn, delay]
  );
};

const logSearchDirectly = (action: string, description: string, metadata: any) => {
  logService.logAction(ActionCategory.SEARCH, action, description, metadata);
};

const Billed: FC = () => {
  // Track user actions
  const tracking = useTracking();
  
  // Base states
  const [data, setData] = useState<BilledCategory[]>([]);
  const [customers, setCustomers] = useState<BilledCustomer[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("2026");
  const [fromMonth, setFromMonth] = useState<string>("");
  const [toMonth, setToMonth] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Additional data states for child components
  const [sumData, setSumData] = useState<SumData[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [sumLoading, setSumLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Agent related states
  const [userRole, setUserRole] = useState<string>("");
  const [agents, setAgents] = useState<Agent[]>([]);
  const [currentAgentId, setCurrentAgentId] = useState<string>("");
  const [authorizedAgentIds, setAuthorizedAgentIds] = useState<string[]>([]);
  const [agentiOptions, setAgentiOptions] = useState<SelectOption[]>([]);
  const [agentFilters, setAgentFilters] = useState<Agent[]>([]);
  const [showAgentFilter, setShowAgentFilter] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [dataVersion, setDataVersion] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<
    "Categoria" | "Cliente"
  >("Categoria");

  // Drill-down states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState<BilledDetailRow[]>([]);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailSearch, setDetailSearch] = useState('');

  // Refs to track API calls
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const sumRequestIdRef = useRef<string>("");
  const graphRequestIdRef = useRef<string>("");
  const isPageLoading = loading || sumLoading || chartLoading;

  const months = [
    { value: "01", label: "Gennaio" },
    { value: "02", label: "Febbraio" },
    { value: "03", label: "Marzo" },
    { value: "04", label: "Aprile" },
    { value: "05", label: "Maggio" },
    { value: "06", label: "Giugno" },
    { value: "07", label: "Luglio" },
    { value: "08", label: "Agosto" },
    { value: "09", label: "Settembre" },
    { value: "10", label: "Ottobre" },
    { value: "11", label: "Novembre" },
    { value: "12", label: "Dicembre" },
  ];



  // Track request signature to avoid duplicate calls
  const getAgentCodesSignature = useCallback((codes: string[]) => {
    return codes.sort().join(",");
  }, []);

  // Sorting handler
  const handleSort = useCallback(
    (field: string) => {
      const isAscending = sortField === field && sortDirection === "asc";
      setSortField(field);
      setSortDirection(isAscending ? "desc" : "asc");
    },
    [sortField, sortDirection]
  );

  // Memoized filtered and sorted data
  const sortedData = useMemo(() => {
    const dataset =
      selectedCategory === "Categoria"
        ? data.filter((item) => item.year === selectedYear)
        : customers.filter((customer) => customer.year === selectedYear);

    const enrichedData = dataset.map((item) => {
      const previousYearData =
        selectedCategory === "Categoria"
          ? data.find(
              (d) =>
                d.year === String(Number(selectedYear) - 1) &&
                d.descrizione_categoria === item.descrizione_categoria
            )
          : customers.find(
              (c) =>
                c.year === String(Number(selectedYear) - 1) &&
                c.ragione_sociale === item.ragione_sociale
            );

      const quantita_precedente = previousYearData?.total_quantita || 0;
      const fatturato_precedente = previousYearData?.prezzo || 0;
      const variazione = previousYearData?.prezzo
        ? ((item.prezzo - previousYearData.prezzo) / previousYearData.prezzo) *
          100
        : 0;

      return {
        ...item,
        quantita_precedente,
        fatturato_precedente,
        variazione,
      };
    });

    // Filter based on the search term
    const filteredData = searchTerm.trim()
      ? enrichedData.filter((item) => {
          const value =
            selectedCategory === "Categoria"
              ? item.descrizione_categoria
              : item.ragione_sociale;
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        })
      : enrichedData;

    if (!sortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      const valueA = a[sortField as keyof typeof a];
      const valueB = b[sortField as keyof typeof b];

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      } else if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      return 0;
    });
  }, [
    data,
    customers,
    selectedYear,
    selectedCategory,
    searchTerm,
    sortField,
    sortDirection,
  ]);

  // Previous year total, summed independently over the full prior-year dataset
  // rather than derived from matching current-year rows (which drops any
  // category/customer that had revenue last year but none this year).
  const previousYearTotal = useMemo(() => {
    const prevYear = String(Number(selectedYear) - 1);
    const source = selectedCategory === "Categoria" ? data : customers;
    return source
      .filter((item) => item.year === prevYear)
      .reduce((sum, item) => sum + item.prezzo, 0);
  }, [data, customers, selectedCategory, selectedYear]);

  const handleCSVDownload = useCallback(() => {
    // Process agent codes, splitting any comma-separated values
    let agentCodesToUse = [];
    
    if (selectedAgent) {
      // Single selected agent - check if it has multiple codes
      if (typeof selectedAgent === 'string' && selectedAgent.includes(',')) {
        agentCodesToUse = selectedAgent.split(',').map(code => code.trim()).filter(Boolean);
      } else {
        agentCodesToUse = [selectedAgent].filter(Boolean);
      }
    } else if (authorizedAgentIds.length > 0) {
      // All authorized agents - check each for multiple codes
      agentCodesToUse = authorizedAgentIds.flatMap(code => {
        if (typeof code === 'string' && code.includes(',')) {
          return code.split(',').map(c => c.trim()).filter(Boolean);
        }
        return code ? [code] : [];
      });
    }
    
    // Remove duplicates
    agentCodesToUse = [...new Set(agentCodesToUse)];
    
    // Se è stato selezionato solo il mese iniziale, consideriamo dicembre come mese finale
    const effectiveToMonth = (!toMonth && fromMonth) ? "12" : toMonth;
    // Se è stato selezionato solo il mese finale, consideriamo gennaio come mese iniziale
    const effectiveFromMonth = (!fromMonth && toMonth) ? "01" : fromMonth;
    
    // Prepara date in formato ISO se sono stati selezionati i mesi
    const from = effectiveFromMonth ? `${selectedYear}-${effectiveFromMonth}-01` : "";
    const to = effectiveToMonth
      ? `${selectedYear}-${effectiveToMonth}-${moment(`${selectedYear}-${effectiveToMonth}`).daysInMonth()}`
      : "";
    
    console.log('Download CSV with filters:', {
      year: selectedYear,
      codice_agente: agentCodesToUse,
      from,
      to,
      effectiveFromMonth,
      effectiveToMonth
    });
    
    // Passa anche le date ISO per consistenza
    downloadCSV(data, customers, selectedYear, agentCodesToUse, from, to);
  }, [data, customers, selectedYear, selectedAgent, authorizedAgentIds, fromMonth, toMonth]);
  

  // Fetch agents data - only called once on mount
  const fetchUserData = async (): Promise<void> => {
    try {
      const { user, isSupervisor, agentCodes, agents: fetchedAgents } = await fetchUserInfo();
      
      if (!user) {
        console.error("Unable to fetch user data");
        return;
      }
      
      // Set user role
      const role = isSupervisor ? "supervisor" : "agent";
      setUserRole(role);
      
      // Set current agent ID (first code if multiple)
      const currentAgentCode = agentCodes.length > 0 ? agentCodes[0] : "";
      if (currentAgentCode) {
        setCurrentAgentId(currentAgentCode);
      }
      
      // Format agents for the component
      if (role === "supervisor") {
        const formattedAgents = fetchedAgents.map(agent => ({
          db2_user_id: agent.db2_user_id,
          first_name: agent.first_name,
          last_name: agent.last_name,
        }));
        
        setAgents(formattedAgents);
        setShowAgentFilter(true);
      }
      
      // Create select options for agent dropdown
      let agentOptions: SelectOption[] = [];
      
      if (isSupervisor && fetchedAgents.length > 0) {
        // For supervisors: all agents from group
        agentOptions = fetchedAgents.map((agent) => ({
          value: agent.db2_user_id,
          label: `${agent.first_name} ${agent.last_name}`,
          db2_user_id: agent.db2_user_id,
        }));
      } else if (currentAgentCode) {
        // For regular agents: only their own code
        agentOptions = [
          {
            value: currentAgentCode,
            label: `${user.firstName} ${user.lastName}`,
            db2_user_id: currentAgentCode,
          },
        ];
      }
      
      // Define authorized IDs for API calls based on role
      let authorizedIds = [];
      if (isSupervisor) {
        // For supervisors: collect all agent codes, handling multiple codes per agent
        authorizedIds = fetchedAgents.flatMap(agent => {
          // If agent has multiple codes as a comma-separated string
          if (hasMultipleCodes(agent.db2_user_id)) {
            return splitAgentCodes(agent.db2_user_id);
          }
          return [agent.db2_user_id];
        }).filter(Boolean);
      } else {
        // For agents: use their own codes (might be multiple)
        authorizedIds = agentCodes;
      }
      
      setAuthorizedAgentIds(authorizedIds);
      setAgentiOptions(agentOptions);
      setAgentFilters(fetchedAgents);
      
      console.log("User role:", role);
      console.log("Current agent ID:", currentAgentCode);
      console.log("Authorized agent IDs:", authorizedIds);
      console.log("Agent options:", agentOptions);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // Basic implementation of fetch sum data
  const _fetchSumData = useCallback(
    async (codes: string[]) => {
      // Skip if no agent codes
      if (!codes.length) return;

      // Process agent codes to handle any with comma-separated values
      const processedCodes = codes.flatMap(code => {
        if (typeof code === 'string' && code.includes(',')) {
          return code.split(',').map(c => c.trim()).filter(Boolean);
        }
        return code ? [code] : [];
      });
      
      console.log("Processed agent codes for sum request:", processedCodes);

      // Generate a request signature
      const requestSignature = getAgentCodesSignature(processedCodes);

      // Skip if identical request is in flight
      if (requestSignature === sumRequestIdRef.current) return;

      // Set new request signature
      sumRequestIdRef.current = requestSignature;

      setSumLoading(true);
      try {
        console.log("Fetching sum data", new Date().toISOString());

        const requestBody = {
          codice_agente: processedCodes,
        };

        const response = await apiFetch("/nodeapi/sum", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(requestBody),
        });
        console.log("Response status:", response.status);
      if (response.status !== 200 && response.status !== 201) {
        handleLogout(response, "/nodeapi/sum", JSON.stringify(requestBody));
        return;
      }

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        const parsed = JSON.parse(await decryptData(result.encryptedData, result.encryptedKey, result.iv));
        const { categories = [] } = parsed;
        setSumData(categories);
      } catch (error) {
        console.error("Error fetching sum data:", error);
        setSumData([]);
      } finally {
        setSumLoading(false);
      }
    },
    [getAgentCodesSignature]
  );

  // Basic implementation of fetch chart data
  const _fetchChartData = useCallback(
    async (codes: string[]) => {
      // Skip if no agent codes
      if (!codes.length) return;

      // Process agent codes to handle any with comma-separated values
      const processedCodes = codes.flatMap(code => {
        if (typeof code === 'string' && code.includes(',')) {
          return code.split(',').map(c => c.trim()).filter(Boolean);
        }
        return code ? [code] : [];
      });
      
      console.log("Processed agent codes for chart request:", processedCodes);

      // Generate a request signature
      const requestSignature = getAgentCodesSignature(processedCodes);

      // Skip if identical request is in flight
      if (requestSignature === graphRequestIdRef.current) return;

      // Set new request signature
      graphRequestIdRef.current = requestSignature;

      setChartLoading(true);
      try {
        console.log("Fetching graph data", new Date().toISOString());

        let url = "/nodeapi/graph";

        const params = new URLSearchParams();
        if (processedCodes.length > 0) {
          // Invia i codici agente come un unico parametro con valori separati da virgole
          params.append("codice_agente", processedCodes.join(","));
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await apiFetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const result = await response.json();
        const parsed = JSON.parse(await decryptData(result.encryptedData, result.encryptedKey, result.iv));
        setChartData(parsed.billed);
      } catch (error) {
        console.error("Error fetching graph data:", error);
        setChartData([]);
      } finally {
        setChartLoading(false);
      }
    },
    [getAgentCodesSignature]
  );

  // Debounced versions of fetch functions
  const fetchSumData = useDebounce(_fetchSumData, 300);
  const fetchChartData = useDebounce(_fetchChartData, 300);

  const fetchBilledData = async () => {
    // Skip redundant fetches
    if (loading && !isInitialLoad) return;


    console.log("authorizedAgentIds", authorizedAgentIds);

    try {
      setLoading(true);

      // Se è stato selezionato solo il mese iniziale, consideriamo dicembre come mese finale
      const effectiveToMonth = (!toMonth && fromMonth) ? "12" : toMonth;
      // Se è stato selezionato solo il mese finale, consideriamo gennaio come mese iniziale
      const effectiveFromMonth = (!fromMonth && toMonth) ? "01" : fromMonth;
      
      const from = effectiveFromMonth ? `${selectedYear}-${effectiveFromMonth}-01` : "";
      const to = effectiveToMonth
        ? `${selectedYear}-${effectiveToMonth}-${moment(
            `${selectedYear}-${effectiveToMonth}`
          ).daysInMonth()}`
        : "";
        
      console.log("Filtro date effettivo:", { effectiveFromMonth, effectiveToMonth, from, to });

      // Process agent codes properly to ensure consistent format
      let agentCodesForRequest = {};
      
      if (selectedAgent) {
        // Single selected agent - check if it has multiple codes
        if (hasMultipleCodes(selectedAgent)) {
          // Use codice_agente for multiple codes
          agentCodesForRequest = { 
            codice_agente: splitAgentCodes(selectedAgent).filter(Boolean) 
          };
        } else {
          // Single code case
          agentCodesForRequest = { 
            codice_agente: [selectedAgent].filter(Boolean) 
          };
        }
      } else if (authorizedAgentIds.length > 0) {
        // Process all authorized agent IDs to split any comma-separated values
        const processedCodes = authorizedAgentIds.flatMap(code => {
          if (typeof code === 'string' && code.includes(',')) {
            return code.split(',').map(c => c.trim()).filter(Boolean);
          }
          return code ? [code] : [];
        });
        
        // Use codice_agente consistently for all requests
        agentCodesForRequest = { 
          codice_agente: [...new Set(processedCodes)] // Remove duplicates
        };
      }
      
      console.log("Agent codes for billed request:", agentCodesForRequest);
      
      const response = await apiFetch("/nodeapi/billed", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          year: { value: selectedYear, label: selectedYear },
          from,
          to,
          ...agentCodesForRequest
        }),
      });

    
      if (response.status !== 200 && response.status !== 201) {
        handleLogout(response, "/nodeapi/billed", JSON.stringify({
          year: { value: selectedYear, label: selectedYear },
          from,
          to,
          ...agentCodesForRequest
        }));
        return;
      }
       

      const result = await response.json();
      const parsed = JSON.parse(await decryptData(result.encryptedData, result.encryptedKey, result.iv));

      if (parsed) {
        const cleanedCategories = parsed.categories.map((cat) => ({
          ...cat,
          descrizione_categoria: cat.descrizione_categoria.trim(),
        }));

        setData(cleanedCategories);
        setCustomers(parsed.customers);
        setDataVersion((prev) => prev + 1);
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Component initialization - fetch agents only once on mount
  useEffect(() => {
    fetchUserData();
  }, []);

  // Fetch initial data only after agent data is loaded, and only once
/*   useEffect(() => {
    if (userRole && isInitialLoad && !hasLoadedInitialDataRef.current) {
      hasLoadedInitialDataRef.current = true;
      fetchBilledData();
    }
  }, [userRole, isInitialLoad, fetchBilledData]);
 */
  useEffect(() => {
    if (authorizedAgentIds.length > 0) {
      fetchBilledData();
    }
  }, [agentFilters]);

  const agentCodes = useMemo(() => {
    return selectedAgent ? [selectedAgent] : authorizedAgentIds;
  }, [selectedAgent, authorizedAgentIds]);
  

  // Controlled fetch of sum and chart data
  useEffect(() => {
    if (agentCodes.length > 0 && !isInitialLoad) {
      const fetchData = async () => {
        // Fetch sum data without date filters (annual data only)
        await Promise.all([
          fetchSumData(agentCodes),  // No date filters, only annual data
          fetchChartData(agentCodes),
        ]);
      };
      fetchData();
    }
  }, [dataVersion, isInitialLoad]);  // Removed month dependencies

  // Auto-fetch billed data when months change
  useEffect(() => {
    if (authorizedAgentIds.length > 0 && !isInitialLoad) {
      // Debounce per evitare troppe chiamate consecutive
      const timeoutId = setTimeout(() => {
        fetchBilledData();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [fromMonth, toMonth, selectedYear, selectedAgent]);
  
  

  // Reset months when year changes
  useEffect(() => {
    setFromMonth("");
    setToMonth("");
  }, [selectedYear]);

  // Validate and adjust toMonth if needed
  useEffect(() => {
    if (fromMonth && toMonth && fromMonth > toMonth) {
      setToMonth(fromMonth);
    }
  }, [fromMonth, toMonth]);

  const handleSearch = useCallback(() => {
    // Se è stato selezionato solo il mese iniziale, consideriamo dicembre come mese finale
    const effectiveToMonth = (!toMonth && fromMonth) ? "12" : toMonth;
    // Se è stato selezionato solo il mese finale, consideriamo gennaio come mese iniziale
    const effectiveFromMonth = (!fromMonth && toMonth) ? "01" : fromMonth;
    
    const from = effectiveFromMonth ? `${selectedYear}-${effectiveFromMonth}-01` : "";
    const to = effectiveToMonth
      ? `${selectedYear}-${effectiveToMonth}-${moment(`${selectedYear}-${effectiveToMonth}`).daysInMonth()}`
      : "";
      
    // Process agent codes for the request
    let agentCodesForRequest = [];
    
    if (selectedAgent) {
      // Use the selected agent's code(s)
      if (typeof selectedAgent === 'string' && selectedAgent.includes(',')) {
        agentCodesForRequest = selectedAgent.split(',').map(code => code.trim()).filter(Boolean);
      } else {
        agentCodesForRequest = [selectedAgent].filter(Boolean);
      }
    } else if (authorizedAgentIds.length > 0) {
      // Use all authorized agent codes
      agentCodesForRequest = authorizedAgentIds.flatMap(code => {
        if (typeof code === 'string' && code.includes(',')) {
          return code.split(',').map(c => c.trim()).filter(Boolean);
        }
        return code ? [code] : [];
      });
    }
    
    // 1. Usa il sistema standard di tracking (potrebbe essere soggetto a debounce)
    tracking.trackSearch('Ricerca Fatturato ' + new Date().getTime(), undefined, {
      filters: {
        year: selectedYear,
        from,
        to,
        codice_agente: agentCodesForRequest
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    // 2. Usa anche il nostro metodo diretto che bypassa il debounce
    logSearchDirectly('billed_search', 'Ricerca Fatturato (Direct Log)', {
      filters: {
        year: selectedYear,
        from,
        to,
        codice_agente: agentCodesForRequest
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    // Imposta i valori effettivi dei mesi se necessario
    if (fromMonth && !toMonth) {
      setToMonth(effectiveToMonth); // Impostiamo dicembre come mese finale
    } else if (!fromMonth && toMonth) {
      setFromMonth(effectiveFromMonth); // Impostiamo gennaio come mese iniziale
    }
    
    // Se è stato modificato uno dei mesi, esegui la ricerca dopo il prossimo rendering
    if ((fromMonth && !toMonth) || (!fromMonth && toMonth)) {
      setTimeout(() => {
        fetchBilledData();
        // Poi, se abbiamo codici agenti, esegui le chiamate per sum e chart
        if (agentCodes.length > 0) {
          fetchSumData(agentCodes);  // No date filters for sum data
          fetchChartData(agentCodes);
        }
      }, 0);
    } else {
      // Comportamento normale se entrambi i mesi sono impostati o nessuno è impostato
      fetchBilledData();
      // Poi, se abbiamo codici agenti, esegui le chiamate per sum e chart
      if (agentCodes.length > 0) {
        fetchSumData(agentCodes);  // No date filters for sum data
        fetchChartData(agentCodes);
      }
    }
  }, [fetchBilledData, fetchSumData, fetchChartData, agentCodes, fromMonth, toMonth, tracking, selectedYear, selectedAgent, userRole]);
  
  

  const filteredDetailData = useMemo(() => {
    if (!detailSearch.trim()) return detailData;
    const q = detailSearch.toLowerCase();
    return detailData.filter(r =>
      r.descrizione?.toLowerCase().includes(q) ||
      r.marca?.toLowerCase().includes(q) ||
      r.codice_cliente?.toLowerCase().includes(q) ||
      r.descrizione_categoria?.toLowerCase().includes(q) ||
      r.codice_articolo?.toLowerCase().includes(q) ||
      r.descrizione_articolo?.toLowerCase().includes(q)
    );
  }, [detailData, detailSearch]);

  const fetchDetail = useCallback(async (item: BilledCategory | BilledCustomer) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailData([]);
    setDetailSearch('');

    const effectiveToMonth = (!toMonth && fromMonth) ? "12" : toMonth;
    const effectiveFromMonth = (!fromMonth && toMonth) ? "01" : fromMonth;
    const from = effectiveFromMonth ? `${selectedYear}-${effectiveFromMonth}-01` : "";
    const to = effectiveToMonth
      ? `${selectedYear}-${effectiveToMonth}-${moment(`${selectedYear}-${effectiveToMonth}`).daysInMonth()}`
      : "";

    let agentCodesForRequest: string[] = [];
    if (selectedAgent) {
      agentCodesForRequest = hasMultipleCodes(selectedAgent)
        ? splitAgentCodes(selectedAgent)
        : [selectedAgent];
    } else {
      agentCodesForRequest = authorizedAgentIds.flatMap(code =>
        hasMultipleCodes(code) ? splitAgentCodes(code) : code ? [code] : []
      );
    }
    agentCodesForRequest = [...new Set(agentCodesForRequest)];

    const payload: any = {
      year: { value: selectedYear, label: selectedYear },
      from,
      to,
      codice_agente: agentCodesForRequest,
    };

    if (selectedCategory === "Categoria") {
      payload.descrizione_categoria = (item as BilledCategory).descrizione_categoria;
      setDetailTitle((item as BilledCategory).descrizione_categoria);
    } else {
      payload.ragione_sociale = (item as BilledCustomer).ragione_sociale;
      setDetailTitle((item as BilledCustomer).ragione_sociale);
    }

    try {
      const response = await apiFetch('/nodeapi/billed/detail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Network error');
      const result = await response.json();
      const parsed = JSON.parse(await decryptData(result.encryptedData, result.encryptedKey, result.iv));
      setDetailData(parsed.rows || []);
    } catch (error) {
      console.error('Error fetching billed detail:', error);
      setDetailData([]);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedCategory, selectedYear, fromMonth, toMonth, selectedAgent, authorizedAgentIds]);

  const resetFilters = useCallback(() => {
    setFromMonth("");
    setToMonth("");
    setSelectedAgent(null);
    setSearchTerm("");
    fetchBilledData();
    // Also refresh sum data without date filters
    if (agentCodes.length > 0) {
      fetchSumData(agentCodes);  // No date filters
      fetchChartData(agentCodes);
    }
  }, [fetchBilledData, fetchSumData, fetchChartData, agentCodes]);

  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  }, []);

  const formatCurrencyNoDecimals = useCallback((value: number): string => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }, []);



  if (isPageLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
      <BeatLoader color="#2563eb" />
    </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* CSV Button */}
      <div className="fixed sm:absolute top-16 right-2 sm:top-0 sm:right-0 z-40">
        <button
          className="py-2 px-4 items-center justify-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none rounded-l-full min-w-[100px] cu-bt-csv shadow-lg"
          onClick={handleCSVDownload}
          disabled={loading}
        >
          CSV
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 cu-bill-bo relative">
        <div className="sm:max-w-4xl px-5 sm:px-12 mx-auto flex-col justify-between mt-[50px] md:mt-50">
          <h2 className="hidden sm:block text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24">
            Fatturato
          </h2>

          {/* Pass fetched data to child components */}
          {authorizedAgentIds.length > 0 && (
            <>
              <BillingHeader sumData={sumData} loading={sumLoading} />
              <BillingChart chartData={chartData} loading={chartLoading} />
            </>
          )}
        </div>

        <hr className="hidden sm:block mt-10 border-neutral-300 dark:border-neutral-400" />

        {/* Filters section */}
        <div className="px-6 py-8 flex flex-col items-center">
          {/* First row: Year, From Month, To Month */}
          <div className="flex flex-col sm:flex-row w-full sm:w-auto justify-center items-stretch sm:items-center gap-4 mb-4">
            <div className="relative w-full sm:w-48">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-4 py-2 appearance-none bg-white border border-gray-300 cu-bill-bo shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <div className="relative w-full sm:w-48">
              <select
                value={fromMonth || (toMonth ? "01" : "")}
                onChange={(e) => setFromMonth(e.target.value)}
                className="w-full px-4 py-2 appearance-none bg-white border border-gray-300 cu-bill-bo shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Dal mese</option>
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}{!fromMonth && toMonth && month.value === "01" ? " (default)" : ""}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <div className="relative w-full sm:w-48">
              <select
                value={toMonth || (fromMonth ? "12" : "")}
                onChange={(e) => setToMonth(e.target.value)}
                className="w-full px-4 py-2 appearance-none bg-white border border-gray-300 cu-bill-bo shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!fromMonth}
              >
                <option value="">Al mese</option>
                {months
                  .filter(
                    (month) =>
                      !fromMonth || parseInt(month.value) >= parseInt(fromMonth)
                  )
                  .map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}{fromMonth && !toMonth && month.value === "12" ? " (default)" : ""}
                    </option>
                  ))}
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Second row: Agent selection & Buttons */}
          <div className="flex flex-col sm:flex-row w-full sm:w-auto justify-center items-stretch sm:items-center gap-4">
            {userRole === "supervisor" && (
              <div className="cu-wi-se relative w-full sm:w-48">
                <select
                  value={selectedAgent || ""}
                  onChange={(e) => setSelectedAgent(e.target.value || null)}
                  className="w-full px-4 py-2 appearance-none bg-white border border-gray-300 cu-bill-bo shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seleziona Agente</option>
                  {agents.map((agent) => (
                  <option key={agent.db2_user_id} value={agent.db2_user_id}>
                    {agent.first_name} {agent.last_name}
                  </option>
                ))}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            )}

            {/* Filter and Reset Buttons */}
            <div className="relative flex-col gap-4 cu-di-cont">
              <button
                onClick={handleSearch}
                className="py-2 px-5 bg-blue-600 text-white font-semibold hover:bg-blue-700"
              >
                Filtra
              </button>
              <button
                onClick={resetFilters}
                className="py-2 px-5 bg-blue-600 text-white font-semibold hover:bg-blue-700"
              >
                Cancella Filtri
              </button>
            </div>
          </div>
        </div>

        {/* Category/Client toggle */}
        <div className="flex justify-center mb-6">
          <div className="flex border border-gray-300 overflow-hidden">
            <button
              className={`px-8 py-2 text-sm font-medium ${
                selectedCategory === "Categoria"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300"
              }`}
              onClick={() => setSelectedCategory("Categoria")}
            >
              Categoria
            </button>
            <button
              className={`px-8 py-2 text-sm font-medium ${
                selectedCategory === "Cliente"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-300"
              }`}
              onClick={() => setSelectedCategory("Cliente")}
            >
              Cliente
            </button>
          </div>
        </div>

        {/* Search field above the table */}
        {!loading && (
          <div className="px-6">
            <input
              type="text"
              placeholder={`Cerca per ${
                selectedCategory === "Categoria" ? "categoria" : "cliente"
              }...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border-0 border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-2 px-4 focus:outline-none focus:border-blue-500"
            />
          </div>
        )}

        {/* Totali filtrati */}
        {!loading && sortedData.length > 0 && (
          <div className="px-6 mt-6 mb-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
{/*               <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Totali Filtrati
                {fromMonth && ` (${months.find(m => m.value === fromMonth)?.label || ''}`}
                {fromMonth && toMonth && fromMonth !== toMonth && ` - ${months.find(m => m.value === toMonth)?.label || ''}`}
                {fromMonth && `)`}
                {selectedAgent && agents.find(a => a.db2_user_id === selectedAgent) && 
                  ` - ${agents.find(a => a.db2_user_id === selectedAgent)?.first_name} ${agents.find(a => a.db2_user_id === selectedAgent)?.last_name}`
                }
              </h3> */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Fatturato {selectedYear}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrencyNoDecimals(sortedData.reduce((sum, item) => sum + item.prezzo, 0))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Quantità {selectedYear}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {sortedData.reduce((sum, item) => sum + item.total_quantita, 0).toLocaleString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Fatturato {Number(selectedYear) - 1}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrencyNoDecimals(previousYearTotal)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Variazione %</p>
                  <p className={classNames(
                    "text-lg font-bold",
                    (() => {
                      const currentTotal = sortedData.reduce((sum, item) => sum + item.prezzo, 0);
                      const variation = previousYearTotal > 0 ? ((currentTotal - previousYearTotal) / previousYearTotal) * 100 : 0;
                      return variation >= 0 ? "text-green-600" : "text-red-600";
                    })()
                  )}>
                    {(() => {
                      const currentTotal = sortedData.reduce((sum, item) => sum + item.prezzo, 0);
                      const variation = previousYearTotal > 0 ? ((currentTotal - previousYearTotal) / previousYearTotal) * 100 : 0;
                      return variation.toFixed(0);
                    })()}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table section with fixed minimum height */}
        {loading ? (
          <div className="flex justify-center p-4 mt-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto min-h-[300px]">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <SortableHeader
                    field={
                      selectedCategory === "Categoria"
                        ? "descrizione_categoria"
                        : "ragione_sociale"
                    }
                    label={
                      selectedCategory === "Categoria" ? "Categoria" : "Cliente"
                    }
                    onClick={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    field="total_quantita"
                    label="Quantità"
                    onClick={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    field="prezzo"
                    label="Fatturato"
                    onClick={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    field="quantita_precedente"
                    label="Quantità A-1"
                    onClick={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    field="fatturato_precedente"
                    label="Fatturato A-1"
                    onClick={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                  <SortableHeader
                    field="variazione"
                    label="Var. %"
                    onClick={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedData.map((item) => (
                  <tr
                    key={
                      selectedCategory === "Categoria"
                        ? item.descrizione_categoria
                        : item.ragione_sociale
                    }
                    className="cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700"
                    onClick={() => fetchDetail(item)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                      {selectedCategory === "Categoria"
                        ? item.descrizione_categoria
                        : item.ragione_sociale}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-left text-gray-900 dark:text-gray-300">
                      {item.total_quantita}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-left text-gray-900 dark:text-gray-300">
                      {formatCurrency(item.prezzo)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-left text-gray-900 dark:text-gray-300">
                      {item.quantita_precedente}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-left text-gray-900 dark:text-gray-300">
                      {formatCurrency(item.fatturato_precedente)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-left">
                      <span
                        className={classNames(
                          "font-medium",
                          item.variazione && item.variazione > 0
                            ? "text-green-600"
                            : "text-red-600"
                        )}
                      >
                        {item.variazione?.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drill-down modal */}
      {detailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDetailModalOpen(false)}
          />

          {/* Panel — bottom sheet on mobile, centered on desktop */}
          <div className="relative bg-white dark:bg-gray-900 w-full sm:max-w-4xl sm:mx-4 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden">

            {/* Mobile drag handle */}
            <div className="sm:hidden absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-white/40 rounded-full z-10" />

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 pt-6 pb-4 sm:pt-4 flex items-start justify-between flex-shrink-0">
              <div>
                <p className="text-blue-100 text-xs uppercase tracking-widest font-medium">
                  {selectedCategory === "Categoria" ? "Categoria" : "Cliente"} &mdash; {selectedYear}
                </p>
                <h3 className="text-white text-lg font-bold leading-tight mt-0.5">
                  {detailTitle}
                </h3>
              </div>
              <button
                onClick={() => setDetailModalOpen(false)}
                className="text-white/70 hover:text-white transition-colors ml-4 flex-shrink-0 p-1"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* KPI strip */}
            {!detailLoading && detailData.length > 0 && (
              <div className="grid grid-cols-3 divide-x divide-gray-200 dark:divide-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
                <div className="px-4 py-3 text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Righe</p>
                  <p className="text-xl font-bold text-gray-800 dark:text-white tabular-nums">
                    {filteredDetailData.length}
                    {detailSearch && filteredDetailData.length !== detailData.length && (
                      <span className="text-xs font-normal text-gray-400 ml-1">/ {detailData.length}</span>
                    )}
                  </p>
                </div>
                <div className="px-4 py-3 text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Totale Qtà</p>
                  <p className="text-xl font-bold text-gray-800 dark:text-white tabular-nums">
                    {filteredDetailData.reduce((s, r) => s + Number(r.quantita || 0), 0).toLocaleString('it-IT')}
                  </p>
                </div>
                <div className="px-4 py-3 text-center">
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-0.5">Totale</p>
                  <p className="text-xl font-bold text-blue-600 tabular-nums">
                    {formatCurrency(filteredDetailData.reduce((s, r) => s + Number(r.prezzo || 0), 0))}
                  </p>
                </div>
              </div>
            )}

            {/* Search bar */}
            {!detailLoading && detailData.length > 0 && (
              <div className="px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <input
                    type="text"
                    value={detailSearch}
                    onChange={e => setDetailSearch(e.target.value)}
                    placeholder="Filtra per descrizione, marca, articolo, cliente…"
                    className="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white placeholder-gray-400"
                  />
                  {detailSearch && (
                    <button
                      onClick={() => setDetailSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Body */}
            <div className="overflow-auto flex-1">
              {detailLoading ? (
                <div className="flex flex-col justify-center items-center h-48 gap-3">
                  <div className="animate-spin rounded-full h-9 w-9 border-2 border-blue-100 border-t-blue-600" />
                  <p className="text-sm text-gray-400">Caricamento...</p>
                </div>
              ) : detailData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                  <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">Nessun dato disponibile</p>
                </div>
              ) : filteredDetailData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                  <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <p className="text-sm">Nessun risultato per "<span className="font-medium text-gray-600 dark:text-gray-300">{detailSearch}</span>"</p>
                  <button onClick={() => setDetailSearch('')} className="text-xs text-blue-500 hover:underline">Cancella filtro</button>
                </div>
              ) : (
                <>
                  {/* Desktop: table */}
                  <table className="hidden sm:table min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Data</th>
                        {selectedCategory === "Cliente" && (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Categoria</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Cod. Articolo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Descrizione Articolo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Descrizione</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Marca</th>
                        {selectedCategory === "Categoria" && (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Cod. Cliente</th>
                        )}
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Qtà</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Prezzo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {filteredDetailData.map((row, idx) => (
                        <tr
                          key={idx}
                          className={idx % 2 === 0
                            ? 'bg-white dark:bg-gray-900'
                            : 'bg-gray-50/60 dark:bg-gray-800/50'}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-gray-400 dark:text-gray-500 tabular-nums text-xs">
                            {row.data_fatturato ? moment(row.data_fatturato).format('DD/MM/YYYY') : ''}
                          </td>
                          {selectedCategory === "Cliente" && (
                            <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400 text-xs">{row.descrizione_categoria}</td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400 font-mono text-xs">{row.codice_articolo || '—'}</td>
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs max-w-[200px] truncate">{row.descrizione_articolo || '—'}</td>
                          <td className="px-4 py-3 text-gray-900 dark:text-white font-medium max-w-[240px] truncate">{row.descrizione}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {row.marca && (
                              <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium px-2 py-0.5 rounded-full">
                                {row.marca}
                              </span>
                            )}
                          </td>
                          {selectedCategory === "Categoria" && (
                            <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400 font-mono text-xs">{row.codice_cliente}</td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums text-gray-600 dark:text-gray-300">{row.quantita}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-gray-900 dark:text-white">{formatCurrency(row.prezzo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Mobile: cards */}
                  <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700/50">
                    {filteredDetailData.map((row, idx) => (
                      <div key={idx} className="px-4 py-3 flex flex-col gap-1.5">
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white leading-snug flex-1">
                            {row.descrizione}
                          </span>
                          <span className="text-sm font-bold text-blue-600 whitespace-nowrap">
                            {formatCurrency(row.prezzo)}
                          </span>
                        </div>
                        {(row.codice_articolo || row.descrizione_articolo) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {row.codice_articolo && (
                              <span className="font-mono text-xs text-gray-400 dark:text-gray-500">{row.codice_articolo}</span>
                            )}
                            {row.descrizione_articolo && (
                              <span className="text-xs text-gray-600 dark:text-gray-300">{row.descrizione_articolo}</span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          {row.marca && (
                            <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium px-2 py-0.5 rounded-full">
                              {row.marca}
                            </span>
                          )}
                          {selectedCategory === "Cliente" && row.descrizione_categoria && (
                            <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full">
                              {row.descrizione_categoria}
                            </span>
                          )}
                          {selectedCategory === "Categoria" && row.codice_cliente && (
                            <span className="font-mono text-xs text-gray-400 dark:text-gray-500">{row.codice_cliente}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                          <span>{row.data_fatturato ? moment(row.data_fatturato).format('DD/MM/YYYY') : ''}</span>
                          <span>Qtà: <span className="text-gray-700 dark:text-gray-300 font-medium">{row.quantita}</span></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billed;
